import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import {
    Dispatch,
    MutableRefObject,
    RefObject,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../redux/hook'
import { addAlertNote } from '../../../../services/alertServices'
import { Alert, AlertNote, AlertsApiResponse } from './interfaces'
import { matchesAlert, reconcileSavedNote } from './utils'

export type UseAlertNotesResult = {
    editingNoteAlertId: string | null
    setEditingNoteAlertId: Dispatch<SetStateAction<string | null>>
    noteValue: string
    setNoteValue: Dispatch<SetStateAction<string>>
    editableRef: RefObject<HTMLDivElement | null>
    initialEditableContentRef: MutableRefObject<string>
    handleSaveNote: (alert: Alert) => Promise<void>
    handleStopPropagation: (e: React.BaseSyntheticEvent) => void
    handleEditNoteClick: (id: string, text: string) => void
    handleCancelNoteEdit: (e?: React.BaseSyntheticEvent) => void
    handleSaveNoteClick: (e: React.MouseEvent, alert: Alert) => void
    handleNewNoteFocus: (alertId: string) => void
}

/**
 * Add/edit-note state and mutations for the Acknowledgment Status panel,
 * including the optimistic cache update against the shared alerts query.
 */
export const useAlertNotes = (
    orgId: string,
    queryKey: QueryKey,
    canWrite: boolean,
): UseAlertNotesResult => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const user = useAppSelector((state) => state.user.user)

    const [editingNoteAlertId, setEditingNoteAlertId] = useState<string | null>(
        null,
    )
    const [noteValue, setNoteValue] = useState('')
    const editableRef = useRef<HTMLDivElement | null>(null)
    const initialEditableContentRef = useRef('')

    useEffect(() => {
        if (!editingNoteAlertId || !editableRef.current) return
        editableRef.current.innerHTML = initialEditableContentRef.current
    }, [editingNoteAlertId])

    const handleSaveNote = useCallback(
        async (alert: Alert) => {
            if (!orgId || !noteValue.trim()) return

            const newNote: AlertNote = {
                id: `temp-${Date.now()}`,
                noteText: noteValue.trim(),
                userId: user?.id || '',
                userEmail: user?.email || '',
                createdAt: new Date().toISOString(),
            }

            // Optimistic update — append note to alert's notes array
            queryClient.setQueryData<InfiniteData<AlertsApiResponse>>(
                queryKey,
                (old) => {
                    if (!old) return old
                    return {
                        ...old,
                        pages: old.pages.map((page) => ({
                            ...page,
                            alerts: page.alerts.map((a: Alert) => {
                                if (!matchesAlert(a, alert)) return a
                                return {
                                    ...a,
                                    notes: [newNote, ...(a.notes || [])],
                                }
                            }),
                        })),
                    }
                },
            )

            const payload = {
                entityId: alert.entityId,
                ruleId: alert.ruleId,
                alertId: alert.id,
                alertTimestamp: alert.timestamp,
                noteText: noteValue.trim(),
            }

            try {
                const savedNote = await addAlertNote(orgId, payload)

                // Reconcile the optimistic temp id with the server-assigned
                // UUID so a later edit doesn't send the temp id to PUT
                // /alerts/note (that endpoint requires a real UUID).
                queryClient.setQueryData<InfiniteData<AlertsApiResponse>>(
                    queryKey,
                    (old) =>
                        old && {
                            ...old,
                            pages: reconcileSavedNote(
                                old.pages,
                                alert,
                                newNote.id,
                                savedNote,
                            ),
                        },
                )

                enqueueSnackbar(t('Note saved successfully'), {
                    variant: 'success',
                })
            } catch (err) {
                console.error('Failed to save note:', err)
                enqueueSnackbar(t('Failed to save note'), {
                    variant: 'error',
                })
                // Revert on failure
                queryClient.invalidateQueries({ queryKey })
            }

            initialEditableContentRef.current = ''
            if (editableRef.current) {
                editableRef.current.innerHTML = ''
            }
            setEditingNoteAlertId(null)
            setNoteValue('')
        },
        [orgId, noteValue, queryClient, queryKey, t, user?.id, user?.email],
    )

    const handleStopPropagation = useCallback((e: React.BaseSyntheticEvent) => {
        e.stopPropagation()
    }, [])

    /**
     * Opens the inline editor for an existing note, pre-filled with its text.
     * Saving from here does NOT mutate the original note — it creates a fresh
     * note (via handleSaveNote → addAlertNote), leaving the original record and
     * its timestamp intact. Editing an alert note is therefore just "add a new
     * note seeded from the old one" (UBQ-5178).
     */
    const handleEditNoteClick = useCallback(
        (id: string, text: string) => {
            if (canWrite) {
                initialEditableContentRef.current = text || ''
                setEditingNoteAlertId(id)
                setNoteValue(text || '')
            }
        },
        [canWrite],
    )

    const handleCancelNoteEdit = useCallback((e?: React.BaseSyntheticEvent) => {
        e?.stopPropagation()
        initialEditableContentRef.current = ''
        if (editableRef.current) {
            editableRef.current.innerHTML = ''
        }
        setEditingNoteAlertId(null)
        setNoteValue('')
    }, [])

    const handleSaveNoteClick = useCallback(
        (e: React.MouseEvent, alert: Alert) => {
            e.stopPropagation()
            handleSaveNote(alert)
        },
        [handleSaveNote],
    )

    const handleNewNoteFocus = useCallback(
        (alertId: string) => {
            if (editingNoteAlertId !== alertId) {
                initialEditableContentRef.current = ''
                setEditingNoteAlertId(alertId)
                setNoteValue('')
            }
        },
        [editingNoteAlertId],
    )

    return {
        editingNoteAlertId,
        setEditingNoteAlertId,
        noteValue,
        setNoteValue,
        editableRef,
        initialEditableContentRef,
        handleSaveNote,
        handleStopPropagation,
        handleEditNoteClick,
        handleCancelNoteEdit,
        handleSaveNoteClick,
        handleNewNoteFocus,
    }
}
